import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Check, X } from "@phosphor-icons/react";

export default function Departments() {
  const [rows, setRows] = useState([]);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");

  const load = useCallback(() => {
    api.get("/departments/manage").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!newName.trim()) return;
    try { await api.post("/departments", { name: newName.trim() }); toast.success("Reparto aggiunto"); setNewName(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const rename = async (r) => {
    try {
      await api.put(`/departments/${encodeURIComponent(r.name)}`, { name: editName.trim() });
      toast.success("Reparto rinominato: dipendenti aggiornati");
      setEditing(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare il reparto ${r.name}?`)) return;
    try { await api.delete(`/departments/${encodeURIComponent(r.name)}`); toast.success("Reparto eliminato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="departments-page" className="space-y-3 max-w-2xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Reparti</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Aggiungi, rinomina o elimina i reparti. Un reparto con dipendenti assegnati non può essere eliminato.
        </p>
      </div>

      <div className="rounded-sm border border-border bg-card p-3 flex gap-2">
        <Input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Nome nuovo reparto" className="h-9 rounded-sm" data-testid="dept-new-name" />
        <Button className="rounded-sm font-semibold" onClick={add} data-testid="dept-add">
          <Plus size={15} className="mr-1.5" /> Aggiungi
        </Button>
      </div>

      <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
        <table className="w-full text-sm" data-testid="departments-table">
          <thead className="bg-secondary">
            <tr>{["", "Reparto", "Dipendenti"].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name} className="border-t border-border hover:bg-secondary/40" data-testid={`dept-row-${r.name}`}>
                <td className="px-3 py-1.5">
                  <div className="flex justify-start gap-1">
                    {editing === r.name ? (
                      <>
                        <Button size="sm" className="h-7 rounded-sm" onClick={() => rename(r)} data-testid={`dept-save-${r.name}`}><Check size={14} /></Button>
                        <Button size="sm" variant="outline" className="h-7 rounded-sm" onClick={() => setEditing(null)}><X size={14} /></Button>
                      </>
                    ) : (
                      <>
                        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-sm"
                          onClick={() => { setEditing(r.name); setEditName(r.name); }} data-testid={`dept-edit-${r.name}`}>
                          <PencilSimple size={15} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-sm text-destructive"
                          onClick={() => remove(r)} data-testid={`dept-delete-${r.name}`}>
                          <Trash size={15} />
                        </Button>
                      </>
                    )}
                  </div>
                </td>
                <td className="px-3 py-1.5 font-semibold">
                  {editing === r.name
                    ? <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8 rounded-sm" data-testid="dept-edit-input" />
                    : r.name}
                </td>
                <td className="px-3 py-1.5 tabular-nums text-muted-foreground">{r.employees}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={3} className="px-3 py-8 text-center text-muted-foreground">Nessun reparto</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
